import { useOutletContext } from 'react-router-dom';

import { getAiSpendBreakdown } from '../../services/aiSpendService';
import {
  formatCount,
  formatLatency,
  formatMoney,
  formatPercent,
  formatUnitCost,
} from '../../utils/helpers/aiSpendFormat';
import useApiResource from '../../utils/hooks/useApiResource';
import ErrorState from '../DataDisplay/ErrorState';
import AnalyticsTable from './AnalyticsTable';
import CostShareBar from './CostShareBar';
import UsageCell from './UsageCell';

const SHARE_COLOR = '#2C59F8';

/**
 * By feature / provider / model / organization.
 *
 * One component for all four because the API returns one identical row shape
 * for every dimension.
 */
export default function AiSpendBreakdown({ dimension, title, description }) {
  const { queryParams, queryKey } = useOutletContext();
  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) => getAiSpendBreakdown({ dimension, params: queryParams, signal }),
    { deps: [`${dimension}:${queryKey}`] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const rows = data?.results || [];
  const summary = data?.summary || {};

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-6 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <span>
          <span className="text-slate-500 dark:text-slate-400">Total </span>
          <strong className="text-slate-900 dark:text-white">
            {formatMoney(summary.cost_usd)}
          </strong>
        </span>
        <span>
          <span className="text-slate-500 dark:text-slate-400">Calls </span>
          <strong className="text-slate-900 dark:text-white">
            {formatCount(summary.calls)}
          </strong>
        </span>
        <span>
          <span className="text-slate-500 dark:text-slate-400">Distinct </span>
          <strong className="text-slate-900 dark:text-white">{rows.length}</strong>
        </span>
        {summary.unpriced_calls > 0 && (
          <span className="text-amber-700 dark:text-amber-300">
            {formatCount(summary.unpriced_calls)} calls have no price — their cost is
            excluded from the total
          </span>
        )}
      </div>

      <AnalyticsTable
        loading={loading}
        rows={rows}
        columns={[
          { key: 'label', label: title, render: (row) => row.label },
          {
            key: 'cost_usd',
            label: 'Cost',
            render: (row) => formatMoney(row.cost_usd),
            className: 'font-semibold',
          },
          {
            key: 'cost_share',
            label: 'Share',
            render: (row) => (
              <span className="flex items-center gap-2">
                <CostShareBar
                  segments={[
                    { key: row.key, label: row.label, value: row.cost_usd, color: SHARE_COLOR },
                  ]}
                  total={summary.cost_usd}
                />
                <span className="tabular-nums text-xs text-slate-500">
                  {formatPercent(row.cost_share)}
                </span>
              </span>
            ),
          },
          { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
          {
            key: 'usage',
            label: 'Usage',
            render: (row) => <UsageCell usage={row.usage} billingUnit={row.billing_unit} />,
          },
          {
            key: 'unit_cost',
            label: 'Cost / unit',
            render: (row) => (
              <span
                title={
                  row.billing_unit === 'mixed'
                    ? 'This row mixes billing units, so a per-unit cost is not defined.'
                    : undefined
                }
              >
                {formatUnitCost(row.unit_cost_usd, row.billing_unit)}
              </span>
            ),
          },
          {
            key: 'error_rate',
            label: 'Errors',
            render: (row) =>
              row.failed_calls
                ? `${formatCount(row.failed_calls)} (${formatPercent(row.error_rate)})`
                : '0',
          },
          {
            key: 'avg_latency_ms',
            label: 'Avg latency',
            render: (row) => formatLatency(row.avg_latency_ms),
          },
        ]}
      />
    </div>
  );
}
